import { CSSProperties, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useScreen from 'Hooks/useScreen'
import useSignupFlow from 'Hooks/useSignupFlow'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import ModalSymphony from 'elements/ModalSymphony'
import InputSymphony from 'elements/InputSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import Icon from 'elements/Icon'

const SignupFlowModal = ({ className = '' }: { className?: string }) => {
    const { mobileView } = useScreen()
    const { loginWithPopup, isAuthenticated } = useAuth0()
    const {
        isSignupModalOpen,
        anonymousBrandEmail,
        isLoading,
        error,
        closeSignupModal,
        setAnonymousBrandEmail,
        setError,
        setLoading,
        getCurrentConfig
    } = useSignupFlow()

    const config = getCurrentConfig()

    const handleSubmit = async () => {
        // if (!anonymousBrandEmail.trim()) {
        //     setError('Email is required')
        //     return
        // }

        try {
            setLoading(true)
            setError(null)
            
            // // First, prepare the data in the store (link email to brand, store session data)
            // await handleSignup()
            
            // Then handle the Auth0 popup signup
            await loginWithPopup({
                authorizationParams: {
                    screen_hint: 'signup'
                }
            })
            
            // If we get here, authentication was successful
            // The popup will close automatically and user will be authenticated
            // No need to manually close modal - useEffect below will handle it
            
        } catch (error) {
            console.error('Signup submission error:', error)
            
            // Handle specific popup errors
            if (error instanceof Error) {
                if (error.message.includes('popup_closed')) {
                    setError('Signup was cancelled. Please try again.')
                } else if (error.message.includes('popup_blocked')) {
                    setError('Popup was blocked. Please allow popups and try again.')
                } else {
                    setError(error.message)
                }
            } else {
                setError('Signup failed. Please try again.')
            }
        } finally {
            setLoading(false)
        }
    }

    // Close modal automatically when user becomes authenticated
    useEffect(() => {
        if (isAuthenticated && isSignupModalOpen) {
            closeSignupModal()
        }
    }, [isAuthenticated, isSignupModalOpen])

    if (!isSignupModalOpen || !config) return null

    return (
        <ModalSymphony
            className={`signupFlowModal ${className}`}
            isOpen={isSignupModalOpen}
            onClose={closeSignupModal}
            size="medium"
            title={config.title}
            subtitle={config.subtitle}
        >
            <div className="signupFlowContent" style={sty.content}>
                
                {/* Features List */}
                <div className="signupFlowFeatures" style={sty.features}>
                    {config.features.map((feature: string, index: number) => (
                        <div key={index} className="signupFlowFeature" style={sty.feature}>
                            <Icon 
                                icon="check" 
                                size={14} 
                                color={Colors.green}
                                className="featureIcon"
                            />
                            <span className="featureText" style={sty.featureText}>
                                {feature}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Email Input */}
                <div className="signupFlowForm" style={sty.form}>
                    <InputSymphony
                        className="signupFlowEmailInput"
                        label="Email"
                        placeholder="Enter your email"
                        value={anonymousBrandEmail}
                        onChange={(value) => {
                            setAnonymousBrandEmail(value)
                            if (error) setError(null)
                        }}
                        error={error || undefined}
                        disabled={isLoading}
                    />
                    
                    {/* Submit Button */}
                    <ButtonSymphony
                        className="signupFlowSubmitButton"
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!anonymousBrandEmail.trim() || isLoading}
                        style={sty.submitButton}
                    >
                        {isLoading ? (
                            <>
                                <Icon icon="loading" size={16} className="spin" />
                                Signing up...
                            </>
                        ) : (
                            <>
                                <Icon icon={config.icon} size={16} className="ctaIcon" />
                                {config.ctaText}
                            </>
                        )}
                    </ButtonSymphony>
                </div>

                {/* Privacy Notice */}
                <div className="signupFlowPrivacy" style={sty.privacy}>
                    <span style={sty.privacyText}>
                        By signing up, you agree to our terms of service and privacy policy.
                    </span>
                </div>
            </div>
        </ModalSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 0,
    },
    
    features: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 8,
    },
    
    feature: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    
    featureText: {
        fontSize: 14,
        color: Colors.textColor,
        lineHeight: 1.4,
    },
    
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    
    submitButton: {
        height: 44,
        fontSize: 16,
        fontWeight: 600,
        gap: 8,
    },
    
    privacy: {
        textAlign: 'center',
    },
    
    privacyText: {
        fontSize: 12,
        color: Colors.greyDark,
        lineHeight: 1.4,
    },
}

export default SignupFlowModal 